import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { findLang, SWORN_LANGUAGES } from "@/data/sworn-languages";

export const Route = createFileRoute("/sworn-translator/$language/")({
  loader: ({ params }) => {
    const lang = findLang(params.language);
    if (!lang) throw notFound();
    return { lang };
  },
  head: ({ params }) => {
    const lang = findLang(params.language);
    if (!lang) return { meta: [{ title: ctaTitle("Not found", "/sworn-translator/$language", "th")}, { name: "robots", content: "noindex" }] };
    const path = `/sworn-translator/${lang.slug}`;
    const TITLE = `Sworn Translator ภาษา${lang.th} (${lang.en}) — นักแปลสาบานตน | NYC`;
    const DESC = `นักแปลสาบานตนภาษา${lang.th} (${lang.en}) — ${lang.registryTh} /หน้า เสร็จ ${lang.turnaround} พร้อมตราประทับที่ประเทศปลายทางยอมรับตามกฎหมาย — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/sworn-translator/$language", "th")}, { name: "description", content: ctaDesc(DESC, "/sworn-translator/$language", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/sworn-translator/$language", "th")}, { property: "og:description", content: ctaDesc(DESC, "/sworn-translator/$language", "th")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/en${path}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `Sworn Translation — ${lang.en}`, priceFromThb: lang.priceFrom,
        steps: [
          { name: "ตรวจปลายทาง", text: `เลือกประเภทตราประทับที่ปลายทาง${lang.en} ยอมรับ (${lang.registryEn})` },
          { name: "แปลโดย Sworn Translator", text: `นักแปลที่ขึ้นทะเบียน ${lang.registryEn} มีเลขทะเบียนตรวจสอบได้` },
          { name: "QA + ประทับตรา", text: "ตรวจซ้ำโดยผู้เชี่ยวชาญคนที่สอง + ใบ Certificate of Accuracy" },
          { name: "MFA + Apostille (ถ้าจำเป็น)", text: "ต่อยอดกงสุลไทย + Apostille (Hague 15 ธ.ค. 2025) ในแพ็กเกจเดียว" },
          { name: "จัดส่งทั่วโลก", text: "EMS/DHL/FedEx 2–5 วันทำการ" },
        ],
        faqs: [
          { q: `Sworn Translator ภาษา${lang.th} ได้รับการรับรองจากใคร?`, a: lang.registryTh },
          { q: "ต่างจากแปลทั่วไปอย่างไร?", a: `คำแปลของ Sworn Translator ภาษา${lang.th} ใช้เป็นเอกสารทางกฎหมายได้ทันที — ไม่ต้อง Notary ซ้ำเมื่อยื่นในประเทศปลายทาง` },
          { q: "ราคาและเวลา?", a: `เริ่ม ฿${lang.priceFrom.toLocaleString()}/หน้า เสร็จ ${lang.turnaround} — Express 24 ชม. +50%` },
          { q: "ข้อควรระวัง?", a: lang.notes || "ตรวจสอบเงื่อนไขปลายทางก่อนสั่งแปลเสมอ" },
          { q: "รับรอง MFA + Apostille เพิ่มได้ไหม?", a: `ได้ — เพิ่ม MFA ไทยและ Apostille (Hague มีผล 15 ธ.ค. 2025) ให้ Sworn Translation ภาษา${lang.th} ในแพ็กเกจเดียว ลดเวลาเดินเรื่องรวมประมาณ 60% เทียบกับทำแยกทีละขั้นตอน เพราะเราส่ง MFA ควบขนานกับ QA` },
          { q: "รับเอกสารประเภทใด?", a: "คำพิพากษา สัญญา หนังสือมอบอำนาจ พินัยกรรม ทะเบียนบริษัท ทะเบียนสมรส/หย่า สูติบัตร ทรานสคริปต์ ปริญญาบัตร รายงานแพทย์" },
        ],
        breadcrumbs: [
          { name: "Home", path: "/" },
          { name: "Sworn Translator", path: "/sworn-translator" },
          { name: lang.th, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">ไม่พบภาษาที่ค้นหา</h1>      <QuotableStatsStrip cluster="translate" locale="th" variant="light" />
    </main>
  ),
});

function Page() {
  const { lang } = Route.useLoaderData();
  return (
    <ServiceLanding
      badge={`Sworn · ${lang.en}`}
      title={`Sworn Translator ภาษา${lang.th}`}
      subtitle={`นักแปลสาบานตน ${lang.registryTh} — ${lang.notes || "พร้อมตราประทับที่ประเทศปลายทางยอมรับตามกฎหมาย"}`}
      quickAnswer={{
        q: `Sworn Translator ภาษา${lang.th} คือใคร?`,
        a: `นักแปลที่ได้รับการสาบานตนตาม ${lang.registryTh} — คำแปลใช้ทางกฎหมายได้ทันที เริ่ม ฿${lang.priceFrom.toLocaleString()}/หน้า เสร็จ ${lang.turnaround}`,
      }}
      heroBullets={[
        `ทะเบียนตรวจสอบได้ที่ ${lang.registryEn}`,
        `เริ่ม ฿${lang.priceFrom.toLocaleString()}/หน้า`,
        `เสร็จ ${lang.turnaround}`,
        "ต่อยอด MFA + Apostille ในแพ็กเกจเดียว",
      ]}
      whoFor={{
        th: [`ผู้ยื่นเอกสารต่อหน่วยงานที่ใช้ภาษา${lang.th}`, "นักเรียนต่างประเทศ", "บริษัทเซ็นสัญญาข้ามชาติ"],
        foreign: [`Documents for ${lang.en}-speaking authorities`],
      }}
      steps={[
        { name: "ประเมินราคา", text: "แจ้งประเภทและปลายทาง — ประเมินภายใน 30 นาที" },
        { name: "แปลโดย Sworn", text: `นักแปลขึ้นทะเบียน ${lang.registryEn}` },
        { name: "QA 4-eyes", text: "ตรวจซ้ำโดยผู้เชี่ยวชาญที่สอง" },
        { name: "ประทับตราสาบานตน", text: "พร้อม Certificate of Accuracy" },
        { name: "MFA/Apostille (ถ้าต้องการ)", text: "รวบจบในงานเดียว" },
      ]}
      pricing={[
        { label: `แปลสาบานตน ${lang.th}`, price: `${lang.priceFrom.toLocaleString()}/หน้า`, note: lang.turnaround },
        { label: "เร่งด่วน 24 ชม.", price: "+50%", note: "ตามคิว" },
        { label: "MFA legalization", price: "800/เอกสาร", note: "ประทับตรา MFA" },
        { label: "Apostille (Hague)", price: "1,500/เอกสาร", note: "สำหรับประเทศสมาชิก Hague" },
      ]}
      faqs={[
        { q: "ทะเบียนตรวจสอบได้ที่ไหน?", a: lang.registryTh },
        { q: "ราคาและเวลา?", a: `เริ่ม ฿${lang.priceFrom.toLocaleString()}/หน้า เสร็จ ${lang.turnaround}` },
        { q: "ข้อควรระวัง?", a: lang.notes || "ตรวจเงื่อนไขปลายทางก่อนสั่งแปล" },
        { q: "ต่อ MFA + Apostille ได้ไหม?", a: `ได้ครบในแพ็กเกจเดียว — เพิ่ม MFA ไทย (กงสุล) และ Apostille (Hague มีผล 15 ธ.ค. 2025) ให้ Sworn Translation ภาษา${lang.th} ในเวิร์กโฟลว์เดียว ลดขั้นตอนส่งเอกสารและประหยัดเวลารวม 3–5 วันทำการ` },
        { q: "ส่งงานอย่างไร?", a: `EMS ฟรีในไทย 1–2 วันทำการ พร้อม tracking; ส่งต่างประเทศด้วย DHL/FedEx Express 2–5 วันทำการครอบคลุม 220+ ประเทศ และแนบไฟล์ PDF ลงลายเซ็นดิจิทัลของงานแปลภาษา${lang.th} ทางอีเมลในวันเดียวกัน ไม่มีค่าใช้จ่ายเพิ่ม` },
      ]}
      relatedLinks={[
        { to: "/sworn-translator", label: "← Sworn Translator hub" },
        ...SWORN_LANGUAGES.filter((x) => x.slug !== lang.slug).slice(0, 5).map((x) => ({
          to: `/sworn-translator/${x.slug}` as const, label: `Sworn ${x.th}`,
        })) as any,
        { to: "/moj-translator", label: "MoJ Sworn Translator" },
      ]}
    />
  );
}
